import {
  Badge,
  cn,
  Icon,
  type TableColumn,
  Tooltip,
  Typography,
} from "@kyan/ui";
import Link from "next/link";
import React from "react";

import {
  ChangeIndicator,
  type ChangeIndicatorProps,
} from "#/components/ChangeIndicator/ChangeIndicator";
import { LoadOrRender } from "#/components/LoadOrRender/LoadOrRender";
import { SizeDisplay } from "#/components/SizeDisplay/SizeDisplay";
import {
  getOptionConstraints,
  getPerpSizeDisplayPrecision,
} from "#/config/orders";
import { routes } from "#/config/routes";
import { tooltips } from "#/config/tooltips";
import {
  type ChangeIndicatorValues,
  ComboPricesRowType,
  DeltaType,
  type PricesTableEntry,
} from "#/types/combo";
import { type TradeFee } from "#/types/fees";
import { MarketPair } from "#/types/markets";
import { getImpactColor } from "#/utils/getImpactColor";
import {
  formatInstrumentName,
  getPerpInstrumentProperties,
  isOptionInstrument,
} from "#/utils/instrument.utils";
import { isBuy } from "#/utils/marketActionConverter";
import { getUnderlyingSymbolFromMarketPair } from "#/utils/marketPair";
import { match } from "#/utils/match";
import { isNumeric } from "#/utils/number.utils";
import { formatCurrency, formatValue } from "#/utils/valueFormatters";

export const totalSummaryInstrument = "Total Summary";
export const optionsSummaryInstrument = "Options Summary";

enum CellType {
  IMr = "IMr",
  MMr = "MMr",
  Other = "Other",
}

interface DeltaSlippageWithTooltipProps {
  deltaType?: DeltaType;
  isMaxSlippageExceeded?: boolean;
}

interface PerpLegPriceWithTooltip {
  price?: string;
  isBuy: boolean;
}

export const DeltaSlippageWithTooltip: React.FC<
  DeltaSlippageWithTooltipProps
> = ({ deltaType, isMaxSlippageExceeded }) => {
  return (
    <>
      {!isMaxSlippageExceeded ? (
        <div className="flex items-center gap-2">
          <Typography>{`${deltaType} ∆  Slippage`}</Typography>

          <Tooltip
            avoidCollisions
            side="right"
            align="center"
            content={
              <div className="m-1 grid gap-1">
                <Typography size={12}>Delta-based slippage.</Typography>
                <div className="flex gap-1">
                  <Typography size={12}>See</Typography>
                  <Link href={routes.portfolio.settings + "#slippage"}>
                    <Typography
                      size={12}
                      className="text-brandBlue hover:text-primary transition-colors"
                    >
                      settings.
                    </Typography>
                  </Link>
                </div>
              </div>
            }
          >
            <Icon size="sm" name="question" className="text-textSecondary" />
          </Tooltip>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Typography className="text-warning text-2xs">
            {`Max Slippage Exceeded - ${deltaType} ∆`}
          </Typography>

          <Tooltip
            avoidCollisions
            side="right"
            align="center"
            content={
              <div className="m-1 grid max-w-100 gap-1">
                <Typography primary size={12}>
                  Max Slippage Exceeded
                </Typography>
                <Typography>
                  {
                    "Combo orders are Fill-Or-Kill Market orders. Order won't fill if any of the legs has an orderbook price above the slippage threshold for this delta. Consider filling individual legs with limit orders."
                  }
                </Typography>
              </div>
            }
          >
            <Icon size="sm" name="question" className="text-textSecondary" />
          </Tooltip>
        </div>
      )}
    </>
  );
};

export const PerpSlippageTooltip: React.FC<PerpLegPriceWithTooltip> = ({
  price,
  isBuy,
}) => {
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1">
        <Typography
          size={12}
          className="w-14"
        >{`${isBuy ? "Max" : "Min"} price: `}</Typography>
        <Typography primary size={12}>
          {formatCurrency(Number(price))}
        </Typography>
      </div>
      <Tooltip
        avoidCollisions
        side="right"
        align="center"
        content={
          <div className="m-1 grid gap-1">
            <Typography size={12}>Market-order Perp Slippage.</Typography>
            <div className="flex gap-1">
              <Typography size={12}>See</Typography>
              <Link href={routes.portfolio.settings}>
                <Typography size={12} className="text-brandBlue">
                  settings.
                </Typography>
              </Link>
            </div>
          </div>
        }
      >
        <Icon size="sm" name="question" className="text-textSecondary" />
      </Tooltip>
    </div>
  );
};

const OptionalChangeIndicator = ({
  values,
  template,
  isLoading,
  rowType,
  cellType = CellType.Other,
}: {
  values?: ChangeIndicatorValues | number;
  template?: ChangeIndicatorProps["template"];
  isLoading: boolean;
  rowType: ComboPricesRowType;
  cellType?: CellType;
}) => {
  if (rowType !== ComboPricesRowType.TotalSummary) {
    return isNumeric(values) ? formatValue(values, { template }) : "-";
  }

  const indicatorValues = values as ChangeIndicatorValues;
  const isImrMmr = [CellType.IMr, CellType.MMr].includes(cellType);
  const leftLabelClassName = isImrMmr
    ? getImpactColor(indicatorValues.from)
    : "";
  const rightLabelClassName = isImrMmr
    ? getImpactColor(indicatorValues.to)
    : "";
  const shouldShowWarningIcon = rightLabelClassName === "text-error";

  return (
    <LoadOrRender isLoading={isLoading}>
      <div className="flex gap-1.5">
        {!isImrMmr && (
          <ChangeIndicator
            to={indicatorValues.to}
            from={indicatorValues.from}
            template={template ?? "0%"}
          />
        )}
        {isImrMmr && (
          <>
            {shouldShowWarningIcon && (
              <Icon
                size="xs"
                name="attention"
                className={rightLabelClassName}
              />
            )}
            <ChangeIndicator
              template={template ?? "0%"}
              to={indicatorValues.to * 100}
              from={indicatorValues.from * 100}
              leftLabelClassName={leftLabelClassName}
              rightLabelClassName={rightLabelClassName}
            />
          </>
        )}
      </div>
    </LoadOrRender>
  );
};

export const getColumns = ({
  isLoading,
  comboPerpsFees,
  comboOptionsFees,
  totalWithSlippage,
  perpLegLimitPrice,
}: {
  isLoading: boolean;
  comboPerpsFees: TradeFee[];
  comboOptionsFees: TradeFee[];
  totalWithSlippage: number;
  perpLegLimitPrice?: string;
}): TableColumn<PricesTableEntry>[] => [
  {
    id: "instrument",
    header: "Instrument",
    minWidth: "180px",
    columnClassName: "no-underline pl-4",
    cellClassName: ({ row }) =>
      cn("text-textPrimary pl-4", {
        "text-textSecondary":
          row.instrument === totalSummaryInstrument ||
          row.instrument === optionsSummaryInstrument,
      }),
    cell: ({ instrument }) => formatInstrumentName(instrument),
  },
  {
    id: "side",
    header: "Side",
    tooltip: tooltips.side,
    maxWidth: "60px",
    columnClassName: "flex justify-center",
    cell: ({ side }) =>
      side && (
        <Badge variant={side} className="h-6 capitalize">
          {side}
        </Badge>
      ),
  },
  {
    id: "size",
    header: "Size / Available",
    minWidth: "130px",
    cell: ({ instrument, size, available = 0 }) => {
      if (!size) return "-";

      const isOption = isOptionInstrument(instrument);
      const { underlying } = getPerpInstrumentProperties(instrument);
      const sizeDisplayPrecision = getPerpSizeDisplayPrecision(
        underlying,
        true,
      );

      return (
        <div className="flex gap-1">
          <SizeDisplay
            compact
            value={size}
            error={size > available}
            underlying={isOption ? undefined : underlying}
            precision={isOption ? -1 : sizeDisplayPrecision}
          />
          /
          <SizeDisplay
            compact
            value={available}
            underlying={isOption ? undefined : underlying}
            precision={isOption ? -1 : sizeDisplayPrecision}
          />
        </div>
      );
    },
  },
  {
    id: "imr",
    header: "IMr",
    tooltip: tooltips.imr,
    minWidth: "85px",
    cell: ({ imr, rowType }) => (
      <OptionalChangeIndicator
        values={imr}
        rowType={rowType}
        isLoading={isLoading}
        cellType={CellType.IMr}
      />
    ),
  },
  {
    id: "mmr",
    header: "MMr",
    tooltip: tooltips.mmr,
    minWidth: "85px",
    cell: ({ mmr, rowType }) => (
      <OptionalChangeIndicator
        values={mmr}
        rowType={rowType}
        isLoading={isLoading}
        cellType={CellType.MMr}
      />
    ),
  },
  {
    id: "delta",
    header: "Δ Delta",
    minWidth: "100px",
    tooltip: tooltips.delta,
    cell: ({ delta, rowType }) => (
      <OptionalChangeIndicator
        values={delta}
        template="0.000"
        rowType={rowType}
        isLoading={isLoading}
      />
    ),
  },
  {
    id: "gamma",
    header: "Γ Gamma",
    tooltip: tooltips.gamma,
    minWidth: "100px",
    cell: ({ gamma, rowType }) => (
      <OptionalChangeIndicator
        values={gamma}
        rowType={rowType}
        template="0.0000"
        isLoading={isLoading}
      />
    ),
  },
  {
    id: "vega",
    header: "V Vega",
    tooltip: tooltips.vega,
    minWidth: "105px",
    cell: ({ vega, rowType }) => (
      <OptionalChangeIndicator
        values={vega}
        template="0.00"
        rowType={rowType}
        isLoading={isLoading}
      />
    ),
  },
  {
    id: "theta",
    header: "Θ Theta",
    minWidth: "105px",
    tooltip: tooltips.theta,
    cell: ({ theta, rowType }) => (
      <OptionalChangeIndicator
        values={theta}
        template="0.00"
        rowType={rowType}
        isLoading={isLoading}
      />
    ),
  },
  {
    id: "mark",
    header: "Mark Price",
    tooltip: tooltips.markPrice,
    minWidth: "105px",
    cell: ({ markPrice, rowType, instrument }) => {
      if (rowType !== ComboPricesRowType.OptionsLeg || !markPrice) return "-";
      const pair = instrument?.split("-")[0] as MarketPair;
      const market = getUnderlyingSymbolFromMarketPair(pair);
      const decimals =
        getOptionConstraints(market)?.priceDisplayPrecisionNotional ?? 0;
      return (
        <Typography>
          {formatCurrency(markPrice, { precision: decimals })}
        </Typography>
      );
    },
  },
  {
    id: "legPrice",
    header: "Best Leg Price",
    minWidth: "105px",
    cell: ({ instrument, price, rowType }) => {
      if (rowType !== ComboPricesRowType.OptionsLeg || !price) return "-";
      const pair = instrument?.split("-")[0] as MarketPair;
      const market = getUnderlyingSymbolFromMarketPair(pair);
      const decimals =
        getOptionConstraints(market)?.priceDisplayPrecisionNotional ?? 0;
      return (
        <Typography>
          {formatCurrency(price, { precision: decimals })}
        </Typography>
      );
    },
  },
  {
    id: "netPremiums",
    header: "Net Premiums",
    minWidth: "100px",
    tooltip: tooltips.netPremiums,
    cell: ({
      estimatedExecutionPrice,
      size,
      rowType,
      side,
      instrument,
      netPremium,
    }) => {
      if (rowType === ComboPricesRowType.PerpsLeg) return "-";

      const isOptionsSummary = instrument === optionsSummaryInstrument;
      const isTotalSummary = instrument === totalSummaryInstrument;

      const totalPremiums = isOptionsSummary
        ? Number(netPremium)
        : Number(size) *
          Number(estimatedExecutionPrice) *
          (isBuy(side!) ? -1 : 1);

      return (
        <OptionalChangeIndicator
          template="{±}$0.00"
          isLoading={isLoading}
          values={totalPremiums}
          rowType={isTotalSummary ? ComboPricesRowType.Summary : rowType}
        />
      );
    },
  },
  {
    id: "slippage",
    header: "Premiums with slippage",
    tooltip:
      "Max/min total premiums you are willing to pay/receive as a form of slippage for this market trade.",
    minWidth: "205px",
    cell: ({
      netPremium,
      rowType,
      deltaType,
      side,
      maxBuyPriceAfterSlippage,
      minSellPriceAfterSlippage,
      price,
    }) => {
      const isTotalSummary = rowType === ComboPricesRowType.TotalSummary;
      const isOptionLeg = rowType === ComboPricesRowType.OptionsLeg;
      const isPerpLeg = rowType === ComboPricesRowType.PerpsLeg;

      if (isTotalSummary) return null;
      if (isPerpLeg) {
        return (
          <div>
            <LoadOrRender
              className="w-10"
              isLoading={!perpLegLimitPrice || isLoading}
            >
              <PerpSlippageTooltip
                isBuy={side === "buy"}
                price={perpLegLimitPrice}
              />
            </LoadOrRender>
          </div>
        );
      }

      if (isOptionLeg) {
        const isSlippageExceeded =
          side === "buy"
            ? Boolean(
                maxBuyPriceAfterSlippage &&
                Number(maxBuyPriceAfterSlippage) < Number(price),
              )
            : Boolean(
                minSellPriceAfterSlippage &&
                Number(minSellPriceAfterSlippage) > Number(price),
              );

        return (
          <div>
            <LoadOrRender className="w-10" isLoading={!deltaType || isLoading}>
              <DeltaSlippageWithTooltip
                deltaType={deltaType}
                isMaxSlippageExceeded={isSlippageExceeded}
              />
            </LoadOrRender>
          </div>
        );
      }

      const netPremiumValue = netPremium ?? 0;

      const getBadge = () => {
        if (netPremiumValue === 0) return "Even";

        const isCredit = netPremiumValue > 0;
        return (
          <Badge
            variant={isCredit ? "buy" : "sell"}
            className="h-5 text-[11px] capitalize"
          >
            {isCredit ? "Credit" : "Debit"}
          </Badge>
        );
      };

      return (
        <div className="flex items-center gap-3">
          <div className="w-10.5">{getBadge()}</div>
          <Typography primary>
            <OptionalChangeIndicator
              template="{±}$0.00"
              isLoading={isLoading}
              values={totalWithSlippage}
              rowType={ComboPricesRowType.Summary}
            />
          </Typography>
        </div>
      );
    },
  },
  {
    id: "fee",
    header: "Trade Fee",
    cell: (row) => {
      const optionsFeesTotal = comboOptionsFees.reduce(
        (acc, fee) => acc + fee.totalFee,
        0,
      );
      const perpsFeesTotal = comboPerpsFees.reduce(
        (acc, fee) => acc + fee.totalFee,
        0,
      );
      const total = match([
        [optionsFeesTotal, row.rowType === ComboPricesRowType.Summary],
        [perpsFeesTotal, row.rowType === ComboPricesRowType.PerpsLeg],
        [
          optionsFeesTotal + perpsFeesTotal,
          row.rowType === ComboPricesRowType.TotalSummary,
        ],
      ]);

      return formatCurrency(total);
    },
  },
];
